import { useCallback, useLayoutEffect, useState } from 'react';
import { useUser } from '../context/UserContext';

export interface IUserPreferenceStore<T> {
  read: (userId?: string) => T;
  store: (userId: string, value: T) => void;
  apply: (value: T) => void;
}

/** A setting each user keeps on this device, applied to the page before it paints. */
export const useUserPreference = <T>({ read, store, apply }: IUserPreferenceStore<T>) => {
  const { user } = useUser();
  const userId = user?.id;
  const [value, setValue] = useState<T>(() => read(userId));
  const [loadedUserId, setLoadedUserId] = useState(userId);

  // Signing in as someone else swaps in their own value.
  if (userId !== loadedUserId) {
    setLoadedUserId(userId);
    setValue(read(userId));
  }

  useLayoutEffect(() => {
    apply(value);
  }, [apply, value]);

  const updateValue = useCallback(
    (next: T) => {
      if (userId) store(userId, next);
      setValue(next);
    },
    [store, userId],
  );

  return [value, updateValue] as const;
};
